import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { isSensitive } from "@/lib/collections";
import { fmtTs, toDate } from "@/lib/format";
import { SENSITIVE_AUDIT, restoreValue } from "./audit";
import { UndoDialog } from "./UndoDialog";
import { canSuperEdit } from "./DocumentEditor";

const UNDOABLE = ["update_field", "add_field", "delete_field", "create_document", "delete_document", "bulk_update_field", "undo"];
const short = (s) => (s == null ? "∅" : String(s).length > 90 ? String(s).slice(0, 90) + "…" : String(s));

export function DocHistory({ path, id }) {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [sens, setSens] = useState([]);
  const [undoing, setUndoing] = useState(null);
  const sensitive = isSensitive(path, id);
  useEffect(() => {
    const f = [["entity_type", "==", path], ["entity_id", "==", id]];
    const u1 = adapter.subscribeWhere("activity_logs", f, setLogs);
    const u2 = sensitive ? adapter.subscribeWhere(SENSITIVE_AUDIT, f, setSens) : null;
    return () => { u1(); u2 && u2(); };
  }, [path, id, sensitive]);

  const items = useMemo(() => [...sens, ...logs.filter((l) => !(sensitive && l.sensitive_audit && sens.length))]
    .sort((a, b) => (toDate(b.created_at)?.getTime() || 0) - (toDate(a.created_at)?.getTime() || 0)), [logs, sens, sensitive]);

  return (
    <div data-testid="doc-history">
      {items.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center" data-testid="history-empty">No audited edits for this document yet.</p>}
      <ol className="space-y-2">
        {items.map((e) => {
          const can = canSuperEdit(user) && UNDOABLE.includes(e.action) && (e.action !== "undo" || e.field) && restoreValue(e).ok;
          return (
            <li key={e.id} className="rounded-lg border border-border p-3" data-testid={`history-item-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-foreground">{e.action}{e.field ? <> · <b>{e.field}</b></> : null}</p>
                  <p className="text-[11px] text-muted-foreground">{e.performed_by || "unknown"} · {fmtTs(e.created_at)}{e.undo_of ? " · undo" : ""}</p>
                </div>
                {can
                  ? <Button size="sm" variant="outline" className="h-7 gap-1 shrink-0" onClick={() => setUndoing(e)} data-testid={`history-undo-${e.id}`}><Icons.Undo2 className="h-3.5 w-3.5" /> Undo</Button>
                  : <span className="text-[10px] text-muted-foreground shrink-0">{e.before === "[redacted]" ? "redacted" : ""}</span>}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] font-mono">
                <p className="rounded bg-red-50 text-red-800 px-2 py-1 break-all">{short(e.before_full ?? e.before)}</p>
                <p className="rounded bg-emerald-50 text-emerald-800 px-2 py-1 break-all">{short(e.after_full ?? e.after)}</p>
              </div>
            </li>
          );
        })}
      </ol>
      <UndoDialog path={path} id={id} entry={undoing} sensitive={sensitive} onClose={() => setUndoing(null)} />
    </div>
  );
}
